import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Save, Navigation, Upload, Trash2 } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import { uploadLogoWithValidation, getLogo, removeLogo } from '../utils/logoUpload';
import { API_BASE_URL } from '../api/client';

const Profile = () => {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    fetchProfile();
    // Load existing logo
    const savedLogo = getLogo();
    setCompanyLogo(savedLogo);
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = response.data;
      // Sanitize all fields - convert null/undefined to empty string
      const sanitizedProfile = {
        name: String(data.name || ''),
        email: String(data.email || ''),
        phone: String(data.phone || ''),
        company: String(data.company || ''),
        address: String(data.address || ''),
        city: String(data.city || ''),
        state: String(data.state || ''),
        zipCode: String(data.zipCode || ''),
        country: String(data.country || ''),
      };
      setProfile(sanitizedProfile);
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Fallback if API fails
      setProfile({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      console.log('Sending profile update:', profile);
      const response = await axios.put(`${API_BASE_URL}/users/profile`, profile, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      console.log('Profile updated response:', response.data);
      setMessage('Profile updated successfully!');
      setIsEditing(false);
      // Refetch profile to ensure data is updated
      await fetchProfile();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Error updating profile: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const autoFillLocation = async () => {
    setLoading(true);
    setMessage('');
    
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Using Nominatim API (OpenStreetMap) for reverse geocoding
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
              {
                headers: {
                  'Accept': 'application/json',
                  'User-Agent': 'solaraith-accounts'
                }
              }
            );
            
            const data = await response.json();
            const address = data.address || {};
            
            setProfile(prev => ({
              ...prev,
              address: address.road || address.street || '',
              city: address.city || address.town || address.hamlet || '',
              state: address.state || address.province || '',
              zipCode: address.postcode || '',
              country: address.country || '',
            }));
            
            setMessage('✓ Location auto-filled from GPS!');
            setIsEditing(true);
            setTimeout(() => setMessage(''), 3000);
          } catch (error) {
            console.error('Error fetching location data:', error);
            setMessage('Error: Could not fetch location details');
            setTimeout(() => setMessage(''), 3000);
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          let errorMsg = 'Could not access your location';
          if (error.code === error.PERMISSION_DENIED) {
            errorMsg = 'Location permission denied. Please enable location access in your browser settings.';
          }
          setMessage(errorMsg);
          setLoading(false);
          setTimeout(() => setMessage(''), 3000);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    } else {
      setMessage('Geolocation is not supported by your browser');
      setLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    const result = await uploadLogoWithValidation(file);
    
    if (result.success) {
      setCompanyLogo(result.data);
      setMessage(result.message);
    } else {
      setMessage(result.error);
    }
    
    setLoading(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleRemoveLogo = () => {
    if (window.confirm('Are you sure you want to remove the company logo?')) {
      removeLogo();
      setCompanyLogo(null);
      setMessage('Logo removed successfully!');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  return (
    <Layout pageTitle="My Profile" pageSubtitle="Manage your personal information">
      <div className="max-w-4xl">

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.includes('success')
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {message}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-8 text-white">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <User size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{profile.name}</h2>
                <p className="text-blue-100">{profile.email}</p>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="p-8">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">Personal Information</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Click the 📍 button next to Address to auto-fill your location
                </p>
              </div>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  isEditing
                    ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isEditing ? 'Cancel' : 'Edit'}
              </button>
            </div>

            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={profile.name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full px-4 py-2 border rounded-lg transition ${
                    isEditing
                      ? 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      : 'border-slate-200 bg-slate-50 cursor-not-allowed'
                  }`}
                />
              </div>

              {/* Email and Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center space-x-2">
                    <Mail size={16} />
                    <span>Email</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={profile.email}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2 border rounded-lg transition ${
                      isEditing
                        ? 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                        : 'border-slate-200 bg-slate-50 cursor-not-allowed'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center space-x-2">
                    <Phone size={16} />
                    <span>Phone</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={profile.phone}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2 border rounded-lg transition ${
                      isEditing
                        ? 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                        : 'border-slate-200 bg-slate-50 cursor-not-allowed'
                    }`}
                  />
                </div>
              </div>

              {/* Company and Address */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={profile.company}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2 border rounded-lg transition ${
                      isEditing
                        ? 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                        : 'border-slate-200 bg-slate-50 cursor-not-allowed'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center space-x-2">
                    <MapPin size={16} />
                    <span>Address</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="address"
                      value={profile.address}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`flex-1 px-4 py-2 border rounded-lg transition ${
                        isEditing
                          ? 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                          : 'border-slate-200 bg-slate-50 cursor-not-allowed'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={autoFillLocation}
                      disabled={loading}
                      title="Auto-fill location from current GPS"
                      className="px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white rounded-lg transition flex items-center justify-center"
                    >
                      <Navigation size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* City, State, Zip */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={profile.city}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2 border rounded-lg transition ${
                      isEditing
                        ? 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                        : 'border-slate-200 bg-slate-50 cursor-not-allowed'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    State/Province
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={profile.state}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2 border rounded-lg transition ${
                      isEditing
                        ? 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                        : 'border-slate-200 bg-slate-50 cursor-not-allowed'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Zip/Postal Code
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={profile.zipCode}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2 border rounded-lg transition ${
                      isEditing
                        ? 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                        : 'border-slate-200 bg-slate-50 cursor-not-allowed'
                    }`}
                  />
                </div>
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  value={profile.country}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full px-4 py-2 border rounded-lg transition ${
                    isEditing
                      ? 'border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      : 'border-slate-200 bg-slate-50 cursor-not-allowed'
                  }`}
                />
              </div>
            </div>

            {/* Company Logo Section */}
            <div className="mt-8 pt-8 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Company Logo</h3>
              <p className="text-sm text-slate-600 mb-4">
                Upload your company logo to display on invoices. Recommended size: 200x200px or larger. Max file size: 5MB
              </p>
              
              <div className="flex flex-col md:flex-row gap-6">
                {/* Logo Preview */}
                <div className="flex-1">
                  <div className="w-full h-48 bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center">
                    {companyLogo ? (
                      <img 
                        src={companyLogo} 
                        alt="Company Logo" 
                        className="max-h-40 max-w-full object-contain"
                      />
                    ) : (
                      <div className="text-center">
                        <Upload size={32} className="text-slate-400 mx-auto mb-2" />
                        <p className="text-slate-500 text-sm">No logo uploaded</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Upload Controls */}
                <div className="flex-1 flex flex-col justify-center">
                  <label className="block mb-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={loading}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={(e) => e.currentTarget.previousElementSibling.click()}
                      disabled={loading}
                      className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition font-medium flex items-center justify-center gap-2"
                    >
                      <Upload size={18} />
                      {loading ? 'Uploading...' : 'Choose Logo'}
                    </button>
                  </label>

                  {companyLogo && (
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      disabled={loading}
                      className="w-full px-6 py-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 transition font-medium flex items-center justify-center gap-2"
                    >
                      <Trash2 size={18} />
                      Remove Logo
                    </button>
                  )}

                  <p className="text-xs text-slate-500 mt-4">
                    Supported formats: JPG, PNG, GIF, WebP
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="px-8 py-4 bg-slate-50 border-t border-slate-200 flex justify-end space-x-4">
              <button
                onClick={() => setIsEditing(false)}
                className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition font-medium flex items-center space-x-2"
              >
                <Save size={18} />
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
